import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'

import { Icon } from '@/common/components/Icon'
import { Card } from '@/components/ui'
import { cn } from '@/lib/utils'

interface PaymentMethod {
  label: string
  percentage: number
  color: string
}

interface PaymentMethodsWidgetProps {
  className?: string
  totalSales?: string
  subtitle?: string
  methods?: PaymentMethod[]
  soldTicketsCount?: number
}

const GAUGE_COLORS = ['#05A835', '#C8B800']

const GaugeChart: React.FC<{
  methods: PaymentMethod[]
  totalSales?: string
  soldTicketsCount?: number
}> = ({ methods, totalSales, soldTicketsCount }) => {
  const data = methods.map((m) => ({ name: m.label, value: m.percentage }))
  const filler = {
    name: '_filler',
    value: data.reduce((s, d) => s + d.value, 0) / 2,
  }

  return (
    <div className="relative flex w-full items-center justify-center">
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={[...data, filler]}
            cx="50%"
            cy="70%"
            startAngle={210}
            endAngle={-210}
            innerRadius={90}
            outerRadius={115}
            paddingAngle={0}
            dataKey="value"
            stroke="none"
            cornerRadius={8}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={GAUGE_COLORS[i]} />
            ))}
            <Cell fill="transparent" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {/* Bottom shadow behind arc ends */}
      <div className="pointer-events-none absolute translate-y-[-10px] bottom-[-20px] h-[50px] left-0 right-0 bg-black/90 blur-sm z-10" />
      <div className="absolute bottom-3 flex flex-col items-center gap-[3px] -translate-y-[30px]">
        <span className="text-[22px] font-semibold leading-6 text-monochrome-1">
          {totalSales}
        </span>
        <span className="text-body-medium text-monochrome-2">
          Total {soldTicketsCount} Sales
        </span>
      </div>
    </div>
  )
}

const MethodCard: React.FC<{
  method: PaymentMethod
  align: 'left' | 'right'
}> = ({ method, align }) => (
  <div className="flex flex-1 flex-col justify-between rounded-2xl border border-white/20 bg-white/10 p-5 h-[86px]">
    <div
      className={cn(
        'flex items-end justify-between h-full',
        align === 'right' && 'flex-row-reverse',
      )}
    >
      <div
        className={cn(
          'flex h-full flex-col justify-between',
          align === 'right' && 'items-end',
        )}
      >
        <div
          className="size-4 rounded-full"
          style={{ backgroundColor: method.color }}
        />
        <span className="text-[18px] font-semibold leading-6 text-monochrome-1">
          {method.percentage}%
        </span>
      </div>
      <span className="text-body-medium text-monochrome-2">{method.label}</span>
    </div>
  </div>
)

export const PaymentMethodsWidget: React.FC<PaymentMethodsWidgetProps> = ({
  className,
  totalSales,
  subtitle = 'Last 6 Months',
  methods,
  soldTicketsCount = 0,
}) => {
  return (
    <Card
      className={cn(
        'flex flex-col items-center justify-between border-2 border-white/10 bg-black/60 p-[30px] backdrop-blur-sm',
        className,
      )}
    >
      {/* Header */}
      <div className="flex w-full items-center gap-2.5">
        <div className="flex size-11 shrink-0 items-center relative justify-center rounded-3xl border border-white/20">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-accent-1/40 rounded-3xl blur-sm" />
          <Icon
            icon="wallet"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className="text-accent-1"
          />
        </div>
        <div className="flex flex-col">
          <span className="text-title-xxs text-monochrome-1">
            Payment Methods
          </span>
          <span className="text-body-medium text-monochrome-2">{subtitle}</span>
        </div>
      </div>

      {/* Gauge Chart */}
      <GaugeChart
        methods={methods ?? []}
        totalSales={totalSales ?? ''}
        soldTicketsCount={soldTicketsCount}
      />

      {/* Method Cards */}
      <div className="flex w-full gap-5">
        {methods?.[0] && <MethodCard method={methods?.[0]} align="left" />}
        {methods?.[1] && <MethodCard method={methods?.[1]} align="right" />}
      </div>
    </Card>
  )
}
